import { isPlainQuantity } from './requestQuantity';

const AI_REPORT_STORAGE_KEY = 'maabar_ai_draft';
const AI_REPORT_LEGACY_REQUEST_KEY = 'maabar_request_draft';
const AI_REPORT_RESUME_QUERY = 'openAiReview';

function readStorage(storage) {
  if (typeof window === 'undefined' || !storage) return null;
  try {
    return storage.getItem(AI_REPORT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(storage, value) {
  if (typeof window === 'undefined' || !storage) return;
  try {
    if (value == null) storage.removeItem(AI_REPORT_STORAGE_KEY);
    else storage.setItem(AI_REPORT_STORAGE_KEY, value);
  } catch {}
}

// requests.quantity is a TEXT column that must hold a PLAIN POSITIVE NUMBER —
// isPlainQuantity (lib/requestQuantity), the same test the database prices with.
// normalizeRequestQuantity (a digits-only strip) was REMOVED on 2026-09-30: it
// turned «100 إلى 150» into 100150 as the trader typed. The input now keeps what
// was typed (normalizeQuantityInput only rewrites Arabic-Indic / full-width
// digits) and the submit refuses anything that is not plain.

export function buildIdeaRequestDraft(report = {}, overrides = {}) {
  const baseTitleAr = overrides.title_ar || report.product_name_ar || report.product_name_en || '';
  const baseTitleEn = overrides.title_en || report.product_name_en || report.product_name_ar || '';
  const baseTitleZh = overrides.title_zh || report.product_name_zh || report.product_name_en || report.product_name_ar || '';

  // NEVER prefilled from report.moq (2026-09-24). The AI's "Recommended minimum
  // order quantity" is a sentence, and a trader who did not touch the field
  // shipped it as the quantity: M-10089 went out as «الكمية المطلوبة من العميل
  // 100 إلى 150 قطعة، مع الأخذ بالاعتبار رسوم فتح قالب…». The MOQ is now shown
  // as a hint under the field instead.
  // A stored draft that is not already a plain number is DROPPED, not stripped:
  // a draft saved before this change can hold that sentence, and stripping
  // «100 إلى 150» would invent the quantity 100150. (Was /^\d+$/ until
  // 2026-09-30; isPlainQuantity also keeps a decimal like 2.5.)
  const storedQuantity = String(overrides.quantity ?? '').trim();

  return {
    title_ar: baseTitleAr,
    title_en: baseTitleEn,
    title_zh: baseTitleZh,
    quantity: isPlainQuantity(storedQuantity) ? storedQuantity : '',
    // '' — no unit preselected (2026-09-28); ideaDraftMissing requires one.
    unit: overrides.unit || '',
    description: overrides.description ?? report.request_description ?? report.specs ?? '',
    category: overrides.category || report.category || 'other',
    budget_per_unit: overrides.budget_per_unit ?? '',
    budget_currency: overrides.budget_currency || '',
    // Nothing preselected (2026-09-24), as on the direct form. These used to
    // default to '30' / 'preferred', so every idea request shipped with a
    // payment plan and a sample ask the trader may never have chosen.
    payment_plan: overrides.payment_plan ? String(overrides.payment_plan) : '',
    sample_requirement: overrides.sample_requirement || '',
    // Destination + delivery ask (2026-09-27), the same two fields the direct
    // form and the /request wizard require. Kept in the draft so they survive
    // the sign-in redirect. Never prefilled — the AI does not know them.
    ship_city: overrides.ship_city || '',
    delivery_preference: overrides.delivery_preference || '',
  };
}

// What still blocks submitting the review step, in display order. Empty means
// the draft can be sent. Pure, so the rule is testable apart from the component.
//
// The phone is checked only when `phoneValid` is passed. The component passes
// it once the trader is signed in: the field is not shown to a visitor, whose
// submit is a redirect to sign-in, and a number typed before that redirect
// would be lost with it (the phone is kept out of the stored draft).
export function ideaDraftMissing(draft = {}, { phoneValid } = {}) {
  const missing = [];
  if (!String(draft.title_ar || '').trim() && !String(draft.title_en || '').trim()) missing.push('title');
  // Empty OR not a plain number both block (owner, 2026-09-30). The component
  // tells the two apart for the message: see ideaQuantityInvalid.
  if (!isPlainQuantity(draft.quantity)) missing.push('quantity');
  if (!draft.unit) missing.push('unit');
  if (!draft.payment_plan) missing.push('payment_plan');
  if (!draft.sample_requirement) missing.push('sample_requirement');
  if (!String(draft.ship_city || '').trim()) missing.push('ship_city');
  if (!draft.delivery_preference) missing.push('delivery_preference');
  if (phoneValid !== undefined && !phoneValid) missing.push('phone');
  return missing;
}

// A quantity was TYPED but is not a plain number ("100 to 150", "1,000", "500
// pcs"). The submit shows QUANTITY_MESSAGE for this rather than the generic
// "fill in the required fields", which would not tell the trader what is wrong
// with a field they did fill in. An empty quantity is plain "missing".
export function ideaQuantityInvalid(draft = {}) {
  return String(draft.quantity ?? '').trim() !== '' && !isPlainQuantity(draft.quantity);
}

export function normalizeIdeaFlowDraft(rawValue) {
  if (!rawValue) return null;

  try {
    const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    if (!parsed || typeof parsed !== 'object') return null;

    if (!parsed.requestDraft && (parsed.title_ar || parsed.title_en || parsed.description)) {
      const requestDraft = buildIdeaRequestDraft(parsed, parsed);
      return {
        version: 1,
        phase: parsed.phase || 'report',
        requestDraft,
        report: parsed.report || null,
        messages: parsed.messages || [],
        initialIdea: parsed.initialIdea || parsed.description || '',
        pendingAuth: Boolean(parsed.pendingAuth ?? true),
        lang: parsed.lang || 'ar',
        updatedAt: parsed.updatedAt || new Date().toISOString(),
      };
    }

    const requestDraft = buildIdeaRequestDraft(parsed.report || {}, parsed.requestDraft || {});
    const hasMeaningfulDraft = requestDraft.title_ar || requestDraft.title_en || requestDraft.description;
    if (!hasMeaningfulDraft && !parsed.report) return null;

    return {
      version: parsed.version || 2,
      phase: parsed.phase || 'report',
      requestDraft,
      report: parsed.report || null,
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      initialIdea: parsed.initialIdea || '',
      pendingAuth: Boolean(parsed.pendingAuth),
      lang: parsed.lang || 'ar',
      updatedAt: parsed.updatedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function getBrowserStorages() {
  if (typeof window === 'undefined') {
    return { sessionStorage: null, localStorage: null };
  }
  return {
    sessionStorage: window.sessionStorage,
    localStorage: window.localStorage,
  };
}

export function loadIdeaFlowDraft() {
  const { sessionStorage, localStorage } = getBrowserStorages();
  const sessionValue = readStorage(sessionStorage);
  const localValue = readStorage(localStorage);
  return normalizeIdeaFlowDraft(sessionValue || localValue);
}

export function saveIdeaFlowDraft(payload) {
  const normalized = normalizeIdeaFlowDraft({
    version: 2,
    phase: payload?.phase || 'report',
    requestDraft: payload?.requestDraft || null,
    report: payload?.report || null,
    messages: payload?.messages || [],
    initialIdea: payload?.initialIdea || '',
    pendingAuth: Boolean(payload?.pendingAuth),
    lang: payload?.lang || 'ar',
    updatedAt: new Date().toISOString(),
  });

  if (!normalized) return null;

  const serialized = JSON.stringify(normalized);
  const { sessionStorage, localStorage } = getBrowserStorages();
  writeStorage(sessionStorage, serialized);
  writeStorage(localStorage, serialized);

  if (normalized.requestDraft && sessionStorage) {
    try {
      sessionStorage.setItem(AI_REPORT_LEGACY_REQUEST_KEY, JSON.stringify(normalized.requestDraft));
    } catch {}
  }

  return normalized;
}

export function clearIdeaFlowDraft() {
  const { sessionStorage, localStorage } = getBrowserStorages();
  writeStorage(sessionStorage, null);
  writeStorage(localStorage, null);
  if (sessionStorage) {
    try {
      sessionStorage.removeItem(AI_REPORT_LEGACY_REQUEST_KEY);
    } catch {}
  }
}

export function hasIdeaFlowDraft() {
  return Boolean(loadIdeaFlowDraft());
}

export function getIdeaFlowResumePath() {
  return `/dashboard?${AI_REPORT_RESUME_QUERY}=1`;
}

export function shouldResumeIdeaFlow(search = '') {
  const params = new URLSearchParams(search || '');
  return params.get(AI_REPORT_RESUME_QUERY) === '1';
}
